import React, {
  useCallback, useEffect, useReducer, useState,
} from 'react';
import { Delete, EditNote, EnhancedEncryption } from '@mui/icons-material';
import { Link, Redirect, useLocation } from 'react-router-dom';
import { Button, Chip } from '@mui/material';
import HTML from 'shared/components/HTML';
import { useRequest } from 'shared/hooks';
import { DataFormatter } from 'shared/components/data-display/DataFormatter';
import { TextAreaEditor, TextEditor } from 'shared/components/data-display/editors';
import { getTenantsInUnit, getUnit, updateUnit } from 'shared/api/units';
import {
  createGroupDpsk, deleteGroupDpsk, getGroupDpsk, getSmartzone,
} from 'shared/api/wireless';
import { getUnitPath, unitPath, viewPath } from 'shared/routes/building';
import { dateString } from 'shared/utils';
import { BUTTON_COLORS } from 'shared/constants';
import { CardControls, CardSection } from 'shared/components/cardControls';
import { BoxWrapper, GridWrapper } from 'shared/components/data-display/GridFormatter';
import { searchTickets } from 'shared/api/support';
import { TooltipIconButton } from 'shared/components/abstract/interactables';
import { withStaticSelect } from 'shared/components/data-display/withSelectors';
import NotesManager from 'shared/components/data-display/notesManager';
import { dataReducer, DataReducerActions, initialDataReducerState } from 'shared/reducers';
import UnitTickets from './UnitTickets';
import TenantTable from './TenantTable';
import { checkGroupDpsk, formatUnitName } from '../../../shared/utils/formatters';

const qosEditor = withStaticSelect({
  options: [{
    label: 'USER',
    value: 'USER',
  }, {
    label: 'GROUP',
    value: 'GROUP',
  }],
});

/**
 * @param {Object} props
 * @param {number} props.unitId
 * @param {Object<string, *>} props.buildingState
 * @param {Function} props.setSubtitle
 */
export default function UnitView(props) {
  const {
    unitId,
    setSubtitle,
    buildingState: {
      data: {
        id: buildingId,
        qos,
        auth_method: authMethod,
        dot1x,
      },
      editEnabled,
    },
  } = props;

  const { search } = useLocation();
  const {
    request,
    isLoading,
  } = useRequest();
  const [tenants, setTenants] = useState(undefined);
  const [tickets, setTickets] = useState(undefined);
  const [groupDpsk, setGroupDpsk] = useState(undefined);
  const [editingNotes, setEditingNotes] = useState(false);

  const smartzoneId = dot1x && dot1x.smartzone_id;
  const [state, dispatch] = useReducer(dataReducer, initialDataReducerState);
  const [updateCount, setUpdateCount] = useState(0);
  const reloadUnit = useCallback(() => setUpdateCount((c) => c + 1), []);

  const { data: unit } = state;

  useEffect(() => {
    (async () => {
      const [unitResponse, tenantResponse, ticketResponse] = await Promise.all([
        request(getUnit(unitId)),
        request(getTenantsInUnit(unitId, '?expand=subscription')),
        request(searchTickets(`?building_id=${buildingId}&unit_id=${unitId}&tenant_id=0`)),
      ]);
      if (unitResponse !== undefined) {
        dispatch({
          action: DataReducerActions.set,
          payload: unitResponse,
        });
      }
      if (tenantResponse !== undefined) {
        setTenants(tenantResponse);
      }
      if (ticketResponse !== undefined) {
        setTickets(ticketResponse.rows);
      }
    })();
  }, [unitId, request, updateCount, buildingId]);

  // Load the unit's group dpsk when building has smartzone set to group.
  useEffect(() => {
    (async () => {
      if (!smartzoneId) {
        setGroupDpsk(undefined);
        return;
      }

      const smartzone = await request(getSmartzone(smartzoneId));
      if (smartzone === undefined) return;

      const groupDpskResponse = await request(getGroupDpsk(unitId));
      if (!groupDpskResponse) return;
      const { data } = groupDpskResponse;

      if (data) {
        setGroupDpsk(data);
      } else {
        // Setting to null means the unit should have it, but is missing.
        setGroupDpsk(null);
      }
    })();
  }, [unitId, smartzoneId, request]);

  useEffect(() => {
    if (unit && unit.name) {
      setSubtitle(formatUnitName(unit.name));
    }

    return () => setSubtitle('');
  }, [setSubtitle, unit]);

  const onGroupDpskCreate = async () => {
    let result = await request(createGroupDpsk(unitId), 'Created group key');
    if (!result) return;
    // Need to GET the group dpsk because create response doesn't contain the username.
    result = await request(getGroupDpsk(unitId));
    if (result) setGroupDpsk(result.data);
  };
  const onGroupDpskDelete = async () => {
    // Use unit_id from the group dpsk because it might point to different uplink unit id.
    const result = await request(deleteGroupDpsk(groupDpsk.unit_id), 'Deleted group key');
    if (result) {
      setGroupDpsk(null);
    }
  };
  const groupNameAction = (
    <TooltipIconButton
      message="Create this unit's group wireless key"
      icon={EnhancedEncryption}
      onClick={onGroupDpskCreate}
    />
  );

  const groupKeyDeleteAction = (
    <TooltipIconButton
      message="Delete this unit's group wireless key"
      icon={Delete}
      onClick={onGroupDpskDelete}
      color={BUTTON_COLORS.error}
    />
  );

  const groupKeyFormatter = () => <code>{groupDpsk.dpsk}</code>;

  const groupKeyAgeFormatter = () => {
    const {
      daysOld,
      status,
    } = checkGroupDpsk(groupDpsk);
    if (daysOld < 1) {
      return (
        <Chip
          color={BUTTON_COLORS.success}
          label="Created Today"
          size="small"
        />
      );
    }
    if (status === 'NEW') {
      return (
        <Chip
          color={BUTTON_COLORS.primary}
          label={`${daysOld} day${daysOld === 1 ? '' : 's'}`}
          size="small"
        />
      );
    }
    if (status === 'WARNING') {
      return (
        <Chip
          color={BUTTON_COLORS.warning}
          label={`${daysOld} days`}
          size="small"
        />
      );
    }
    return <Chip color={BUTTON_COLORS.error} label={`${daysOld} days`} size="small" />;
  };

  if (unit && unit.building_id !== buildingId) {
    // This allows us to get to a unit view without knowing the building id.
    return <Redirect to={getUnitPath(unit.building_id, unitId)} />;
  }

  const saveChanges = async () => {
    const body = {
      building_id: buildingId,
      ...state.updatedData,
    };
    const result = await request(updateUnit(unitId, body), 'Updated unit');
    if (result) {
      dispatch({
        action: DataReducerActions.set,
        payload: result,
      });
    }
  };

  const unitEdit = [
    {
      label: 'Name',
      key: 'name',
      editor: TextEditor,
    },
    {
      label: 'Updated At',
      formatter: (u) => u.updated_at && dateString(u.updated_at),
      readOnly: true,
    },
  ];

  const unitNotes = [
    {
      label: 'Legacy Notes',
      key: 'note',
      formatter: (t) => <HTML content={t.note} />,
      editor: TextAreaEditor,
    },
  ];

  if (authMethod.includes('802.1x')) {
    unitEdit.splice(2, 0, {
      label: 'VLAN',
      key: 'vlan',
      jsonKey: 'vlan',
      readOnly: true,
    });
    unitEdit.splice(
      1,
      0,
      {
        label: 'QOS Management',
        key: 'qos_management',
        editor: qosEditor,
      },
    );
  }
  if (groupDpsk !== undefined) {
    if (groupDpsk === null) {
      unitEdit.splice(2, 0, {
        label: 'Group Key',
        readOnly: true,
        actionButton: groupNameAction,
      });
    } else {
      unitEdit.splice(
        2,
        0,
        {
          label: 'Group Key',
          readOnly: true,
          formatter: groupKeyFormatter,
          actionButton: groupKeyDeleteAction,
        },
        {
          label: 'Group Key Username',
          readOnly: true,
          formatter: () => groupDpsk.username,
        },
        {
          label: 'Group Key Age',
          readOnly: true,
          formatter: () => ' ',
          component: groupKeyAgeFormatter(),
        },
      );
    }
  }

  const updateValue = (key, value) => dispatch({
    action: DataReducerActions.update,
    payload: {
      key,
      value,
    },
  });

  return (
    <CardSection editEnabled={editEnabled} saveChanges={saveChanges}>
      <CardControls
        editEnabled={editEnabled}
        clearChanges={() => dispatch({ action: DataReducerActions.clear })}
        dirty={state.dirty}
        disabled={isLoading}
      >
        <Link to={`${viewPath}/${buildingId}${unitPath}${search}`}>
          <button type="button" className="card-controls-link-button">All Units</button>
        </Link>
      </CardControls>
      <BoxWrapper>
        <GridWrapper size={6} title="Unit">
          <DataFormatter
            descriptions={unitEdit}
            editing={editEnabled}
            data={unit}
            updatedData={state.updatedData}
            updateValue={updateValue}
          />
        </GridWrapper>
        <GridWrapper
          size={6}
          title="Notes"
          titleIcon={(
            <Button
              variant="outlined"
              startIcon={<EditNote />}
              onClick={() => {
                if (!unit.id) return;
                setEditingNotes(true);
              }}
              size="small"
            >
              Edit
            </Button>
          )}
        >
          <NotesManager
            entityType="Unit"
            entityID={unitId}
            isEditing={editingNotes}
            onStopEditing={() => setEditingNotes(false)}
            onStartEditing={() => setEditingNotes(true)}
            legacyNotes={unit?.note}
            entityConfig={{
              body: unit,
              key: 'note',
              update: updateUnit,
              dispatch,
            }}
          />
          {unit?.note && (
            <DataFormatter
              descriptions={unitNotes}
              editing={false}
              data={unit}
              updatedData={state.updatedData}
              updateValue={updateValue}
              multiline
              copyable
            />
          )}
        </GridWrapper>
        <TenantTable
          defaultSpeed={qos.description}
          editing={editEnabled}
          reloadUnit={reloadUnit}
          tenants={tenants}
          buildingId={buildingId}
          unitId={unitId}
        />
        <UnitTickets
          buildingId={buildingId}
          tickets={tickets}
          unitId={unitId}
        />
      </BoxWrapper>
    </CardSection>
  );
}
